import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { LuFileText, LuFlame } from 'react-icons/lu';
import { ActivityHeatmap, localDayKey } from './ActivityHeatmap';
import { ButtonLink, Button } from '../../ui/Button';
import { CountUp } from '../../ui/CountUp';
import { Alert, EmptyState, Skeleton } from '../../ui/Feedback';
import { Avatar, VerdictLabel } from '../../ui/Labels';
import { useDocumentTitle, useStrings } from '../../tenant/TenantProvider';
import { useAuthStore } from '../../stores/auth';
import { useCatalog } from '../../stores/catalog';
import { practiceApi } from '../../lib/practiceApi';
import { trackerApi } from '../../lib/trackerApi';
import { formatRelative } from '../../lib/format';

const LEVELS = ['Easy', 'Medium', 'Hard'];
const RECENT_LIMIT = 25;

function Fact({ label, value, suffix, icon: Icon }) {
  return (
    <div className="profile-fact">
      <dt>{label}</dt>
      <dd>
        {Icon && <Icon aria-hidden="true" />}
        {value === null ? '—' : <CountUp value={value} />}
        {suffix && value !== null && <span className="profile-fact__suffix">{suffix}</span>}
      </dd>
    </div>
  );
}

function SolvedPanel({ stats, counts }) {
  const t = useStrings();
  const solved = stats ? LEVELS.reduce((sum, level) => sum + stats[level], 0) : 0;
  return (
    <section className="panel profile-solved lit" aria-labelledby="profile-solved-title">
      <div className="panel__header">
        <h2 id="profile-solved-title" className="panel__title">
          {t('profile.solved')}
        </h2>
        {stats && counts && <span className="panel__meta tabular">{t('profile.solvedOf', { solved, total: counts.total })}</span>}
      </div>
      <div className="panel__body profile-solved__levels">
        {LEVELS.map((level) => {
          const value = stats?.[level] ?? 0;
          const total = counts?.[level] ?? 0;
          return (
            <div key={level} className={`progress-level progress-level--${level.toLowerCase()} profile-level`}>
              <span className="progress-level__name">{t(`difficulty.${level}`)}</span>
              <span className="progress-level__count tabular">
                {stats ? (
                  <>
                    <CountUp value={value} />
                    <span className="subtle">/{total}</span>
                  </>
                ) : (
                  <Skeleton width={36} />
                )}
              </span>
              <span className="progress-level__bar" aria-hidden="true">
                <span style={{ width: `${total ? (value / total) * 100 : 0}%` }} />
              </span>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function LanguagePanel({ submissions }) {
  const t = useStrings();
  const languages = useMemo(() => {
    const tally = new Map();
    submissions.forEach((submission) => {
      const entry = tally.get(submission.language) ?? { language: submission.language, total: 0, accepted: 0 };
      entry.total += 1;
      if (submission.verdict === 'Accepted') entry.accepted += 1;
      tally.set(submission.language, entry);
    });
    return [...tally.values()].sort((a, b) => b.total - a.total);
  }, [submissions]);

  if (languages.length === 0) return null;
  const max = languages[0].total;

  return (
    <section className="panel profile-languages" aria-labelledby="profile-languages-title">
      <div className="panel__header">
        <h2 id="profile-languages-title" className="panel__title">
          {t('profile.languages')}
        </h2>
      </div>
      <ul className="panel__body profile-languages__list">
        {languages.map((entry) => (
          <li key={entry.language} className="profile-language">
            <span className="profile-language__name">{t(`languages.${entry.language}`, { fallback: entry.language })}</span>
            <span className="profile-language__count muted tabular">{t('profile.languageCount', { count: entry.total, accepted: entry.accepted })}</span>
            <span className="profile-language__bar" aria-hidden="true">
              <span style={{ width: `${(entry.total / max) * 100}%` }} />
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

export function ProfilePage() {
  const t = useStrings();
  const { userId } = useParams();
  const user = useAuthStore((state) => state.user);
  const isSelf = !userId || userId === user?.id;
  const profileId = isSelf ? user?.id : userId;
  const daily = useCatalog((state) => state.daily.data);
  const [state, setState] = useState({ status: 'loading', stats: null, counts: null, submissions: [], error: null });
  const [subject, setSubject] = useState(null);

  useDocumentTitle(subject?.username || t('profile.title'));

  const load = useCallback(async () => {
    if (!profileId) return;
    setState((current) => ({ ...current, status: 'loading' }));
    const [stats, counts, submissions, board] = await Promise.all([
      practiceApi.profileStats(profileId),
      practiceApi.questionCounts(),
      practiceApi.userSubmissions(profileId),
      trackerApi.list(),
    ]);
    if (board.success) {
      setSubject(board.data.rows.find((row) => row.userId === profileId) || null);
    }
    if (!stats.success || !counts.success) {
      setState({ status: 'error', stats: null, counts: null, submissions: [], error: (stats.success ? counts : stats).error });
      return;
    }
    setState({
      status: 'ready',
      stats: stats.data,
      counts: counts.data,
      submissions: submissions.success ? submissions.data : [],
      error: null,
    });
  }, [profileId]);

  useEffect(() => {
    load();
    useCatalog.getState().loadDaily();
  }, [load]);

  const activity = useMemo(() => {
    const counts = {};
    state.submissions.forEach((submission) => {
      if (!submission.createdAt) return;
      const key = localDayKey(submission.createdAt);
      counts[key] = (counts[key] ?? 0) + 1;
    });
    return counts;
  }, [state.submissions]);

  const ready = state.status === 'ready';
  const solvedTotal = state.stats ? LEVELS.reduce((sum, level) => sum + state.stats[level], 0) : null;
  const accepted = state.submissions.filter((submission) => submission.verdict === 'Accepted').length;
  const acceptance = state.submissions.length > 0 ? Math.round((accepted / state.submissions.length) * 100) : null;
  const activeDays = Object.keys(activity).length;

  return (
    <div className="page profile-page">
      <header className="panel profile-hero lit">
        <Avatar name={isSelf ? user?.username : subject?.username} size="lg" />
        <div className="profile-hero__identity">
          <h1 className="profile-hero__name">{isSelf ? user?.username : subject?.username}</h1>
          {isSelf && <p className="profile-hero__email">{user?.email}</p>}
        </div>
        <dl className="profile-hero__facts">
          <Fact label={t('profile.factSolved')} value={ready ? solvedTotal : null} />
          <Fact label={t('profile.factSubmissions')} value={ready ? state.submissions.length : null} />
          <Fact label={t('profile.factAcceptance')} value={ready ? acceptance : null} suffix="%" />
          {isSelf && <Fact label={t('profile.factStreak')} value={daily ? daily.streak : null} icon={LuFlame} />}
        </dl>
      </header>

      {state.status === 'error' && (
        <Alert tone="danger" action={<Button size="sm" onClick={load}>{t('common.retry')}</Button>}>
          {t('profile.loadError')} {state.error}
        </Alert>
      )}

      <div className="page-grid profile-grid">
        <div className="profile-main">
          <section className="panel profile-activity" aria-labelledby="profile-activity-title">
            <div className="panel__header">
              <h2 id="profile-activity-title" className="panel__title">
                {t('profile.activity')}
              </h2>
              {ready && <span className="panel__meta">{t('profile.activeDays', { count: activeDays })}</span>}
            </div>
            <div className="panel__body">{ready ? <ActivityHeatmap counts={activity} /> : <Skeleton height={130} />}</div>
          </section>

          <section className="section">
            <h2 className="section-title">{t('profile.recent')}</h2>
            {state.status === 'loading' && (
              <div className="history-skeleton">
                <Skeleton height={44} />
                <Skeleton height={44} />
                <Skeleton height={44} />
              </div>
            )}
            {ready && state.submissions.length === 0 && (
              <div className="panel">
                <EmptyState
                  icon={LuFileText}
                  title={t('profile.emptyTitle')}
                  text={t('profile.emptyText')}
                  action={
                    isSelf ? (
                      <ButtonLink to="/problems" variant="primary" viewTransition>
                        {t('profile.startPractising')}
                      </ButtonLink>
                    ) : undefined
                  }
                />
              </div>
            )}
            {ready && state.submissions.length > 0 && (
              <div className="table-card">
                <table className="data-table profile-table">
                  <thead>
                    <tr>
                      <th>{t('profile.columnProblem')}</th>
                      <th>{t('profile.columnVerdict')}</th>
                      <th className="profile-table__language">{t('profile.columnLanguage')}</th>
                      <th className="is-numeric profile-table__runtime">{t('profile.columnRuntime')}</th>
                      <th className="is-numeric">{t('profile.columnSubmitted')}</th>
                    </tr>
                  </thead>
                  <tbody className="stagger">
                    {state.submissions.slice(0, RECENT_LIMIT).map((submission, index) => (
                      <tr key={submission.id} style={{ '--i': Math.min(index, 12) }}>
                        <td>
                          {submission.questionId ? (
                            <Link to={`/problems/${submission.questionId}`} className="profile-table__problem" viewTransition>
                              {submission.title}
                            </Link>
                          ) : (
                            <span className="profile-table__problem">{submission.title}</span>
                          )}
                        </td>
                        <td>
                          <VerdictLabel verdict={submission.verdict} />
                        </td>
                        <td className="muted profile-table__language">{t(`languages.${submission.language}`, { fallback: submission.language })}</td>
                        <td className="is-numeric muted profile-table__runtime mono">
                          {submission.executionTime === null ? '—' : `${submission.executionTime}s`}
                        </td>
                        <td className="is-numeric muted">{submission.createdAt ? formatRelative(submission.createdAt) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>

        <aside className="page-rail">
          <SolvedPanel stats={state.stats} counts={state.counts} />
          {ready && <LanguagePanel submissions={state.submissions} />}
        </aside>
      </div>
    </div>
  );
}
